import { useRef, useState } from 'react'
import {
  backupFilename,
  buildBackup,
  parseBackup,
  serializeBackup,
} from '../lib/backup'
import { buildLabel } from '../lib/buildInfo'
import type { WorkoutRecord } from '../lib/db'
import type { ThemePreference } from '../lib/theme'

interface Props {
  workouts: WorkoutRecord[]
  theme: ThemePreference
  onThemeChange: (theme: ThemePreference) => void
  onBack: () => void
  onReplaceWorkouts: (workouts: WorkoutRecord[]) => void | Promise<void>
}

const THEME_OPTIONS: { id: ThemePreference; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'System' },
]

export function SettingsPage({
  workouts,
  theme,
  onThemeChange,
  onBack,
  onReplaceWorkouts,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const exportBackup = () => {
    setError(null)
    const backup = buildBackup(workouts)
    const blob = new Blob([serializeBackup(backup)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = backupFilename(backup.exportedAt)
    a.click()
    URL.revokeObjectURL(url)
    setMessage(`Exported ${backup.workouts.length} workout${backup.workouts.length === 1 ? '' : 's'}.`)
  }

  const importBackup = async (file: File) => {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const text = await file.text()
      const backup = parseBackup(text)
      const ok = window.confirm(
        `Replace all local workouts with this backup (${backup.workouts.length} workout${
          backup.workouts.length === 1 ? '' : 's'
        })? This cannot be undone.`,
      )
      if (!ok) {
        setMessage('Import cancelled.')
        return
      }
      await onReplaceWorkouts(backup.workouts)
      setMessage(
        `Restored ${backup.workouts.length} workout${backup.workouts.length === 1 ? '' : 's'} from backup.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not import backup.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <section className="panel settings-page" aria-labelledby="settings-title">
      <header className="history-toolbar">
        <button
          type="button"
          className="history-back-btn"
          onClick={onBack}
          aria-label="Back to muscle map"
        >
          <span aria-hidden="true">←</span>
        </button>
        <div className="history-toolbar-copy">
          <h1 id="settings-title">Settings</h1>
          <p className="muted small">Theme, backups, and build info</p>
        </div>
      </header>

      <div className="settings-stack">
        <section className="settings-block" aria-labelledby="theme-title">
          <h2 id="theme-title">Theme</h2>
          <p className="muted small">Choose light, dark, or match your device setting.</p>
          <div className="settings-theme" role="group" aria-label="Theme">
            {THEME_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={theme === opt.id ? 'settings-theme-btn active' : 'settings-theme-btn'}
                aria-pressed={theme === opt.id}
                onClick={() => onThemeChange(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        <section className="settings-block" aria-labelledby="backup-title">
          <h2 id="backup-title">Data backup</h2>
          <p className="muted small">
            Export a JSON backup of workouts on this device, or restore from a previous export.
            Restore replaces your current log.
          </p>
          <div className="settings-actions">
            <button type="button" className="btn-primary" onClick={exportBackup} disabled={busy}>
              Export backup
            </button>
            <button
              type="button"
              className="btn-ghost"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              Import backup
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="settings-file"
              aria-label="Choose backup JSON file"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void importBackup(file)
              }}
            />
          </div>
          <p className="muted small">{workouts.length} workout{workouts.length === 1 ? '' : 's'} on this device</p>
        </section>

        <section className="settings-block" aria-labelledby="about-title">
          <h2 id="about-title">About</h2>
          <p className="muted small">
            MyoFade stores data only on this device. Build{' '}
            <code className="settings-build">{buildLabel()}</code>
          </p>
        </section>

        {message ? (
          <p className="settings-message" role="status">
            {message}
          </p>
        ) : null}
        {error ? (
          <p className="settings-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </section>
  )
}
